import { useState, useEffect, useMemo } from 'react';
import {
  EnvelopeIcon,
  DevicePhoneMobileIcon,
  PaperAirplaneIcon,
  BeakerIcon,
  UserGroupIcon,
  DocumentTextIcon,
  ChevronDownIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  XMarkIcon,
  ClockIcon,
  MagnifyingGlassIcon,
  UserIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import { supabase } from '../../../lib/supabase';
import { TEMPLATE_TYPES, type EmailTemplateType } from '../../../lib/emailTemplates';
import { useTournament } from '../../TournamentContext';
import { toast } from 'react-hot-toast';

interface Player {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  category: string;
  status: string;
}

interface SendResult {
  email: string;
  success: boolean;
  message: string;
}

type RecipientFilter = 'all' | 'active' | 'category' | 'selected' | 'custom';

const PUSH_ACTIONS = [
  { value: '', label: 'No action (notification only)' },
  { value: '/m/leaderboard', label: 'Go to Leaderboard' },
  { value: '/m/schedule', label: 'Go to Schedule' },
  { value: '/m/registration', label: 'Go to Registration' },
  { value: '/m/sponsors', label: 'Go to Sponsors' },
  { value: '/m/prize-breakdown', label: 'Go to Prize Breakdown' },
  { value: '/m/about', label: 'Go to About' },
  { value: '/m/history', label: 'Go to History' },
  { value: 'custom', label: 'Custom URL...' },
];

export default function MobileAdminMessaging() {
  const { selectedTournament } = useTournament();
  const tournamentId = selectedTournament?.id || '';
  const [activeTab, setActiveTab] = useState<'email' | 'push'>('email');

  return (
    <div className="pb-4">
      {/* Tab switcher */}
      <div className="sticky top-14 z-10 bg-white border-b border-gray-100">
        <div className="flex">
          <button
            onClick={() => setActiveTab('email')}
            className={`flex-1 py-3 text-center text-sm font-medium border-b-2 flex items-center justify-center gap-1.5 transition-colors ${
              activeTab === 'email'
                ? 'border-golf-orange text-golf-orange'
                : 'border-transparent text-gray-500'
            }`}
          >
            <EnvelopeIcon className="w-4 h-4" />
            Email
          </button>
          <button
            onClick={() => setActiveTab('push')}
            className={`flex-1 py-3 text-center text-sm font-medium border-b-2 flex items-center justify-center gap-1.5 transition-colors ${
              activeTab === 'push'
                ? 'border-golf-orange text-golf-orange'
                : 'border-transparent text-gray-500'
            }`}
          >
            <DevicePhoneMobileIcon className="w-4 h-4" />
            Push
          </button>
        </div>
      </div>

      {activeTab === 'email' ? (
        <MobileEmailComposer tournamentId={tournamentId} />
      ) : (
        <MobilePushComposer />
      )}
    </div>
  );
}

function MobileEmailComposer({ tournamentId }: { tournamentId: string }) {
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplateType | null>(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [recipientFilter, setRecipientFilter] = useState<RecipientFilter>('all');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [customEmails, setCustomEmails] = useState('');
  const [players, setPlayers] = useState<Player[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [sending, setSending] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false);
  const [sendResults, setSendResults] = useState<SendResult[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [placeholders, setPlaceholders] = useState<Record<string, string>>({});
  const [selectedPlayerIds, setSelectedPlayerIds] = useState<Set<string>>(new Set());
  const [playerSearch, setPlayerSearch] = useState('');
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');

  useEffect(() => {
    if (tournamentId) fetchPlayers();
  }, [tournamentId]);

  useEffect(() => {
    if (selectedTemplate) {
      setSubject(selectedTemplate.defaultSubject);
      const initial: Record<string, string> = {};
      selectedTemplate.placeholders.forEach(p => {
        initial[p.key] = p.example;
      });
      setPlaceholders(initial);
    }
  }, [selectedTemplate]);

  const fetchPlayers = async () => {
    const { data } = await supabase
      .from('players')
      .select('id, first_name, last_name, email, category, status')
      .eq('tournament_id', tournamentId);

    if (data) {
      setPlayers(data);
      const cats = [...new Set(data.map(p => p.category).filter(Boolean))];
      setCategories(cats);
    }
  };

  const getRecipients = (): { email: string; name: string }[] => {
    switch (recipientFilter) {
      case 'all':
        return players.map(p => ({ email: p.email, name: `${p.first_name} ${p.last_name}` }));
      case 'active':
        return players
          .filter(p => p.status?.toLowerCase() === 'active')
          .map(p => ({ email: p.email, name: `${p.first_name} ${p.last_name}` }));
      case 'category':
        return players
          .filter(p => p.category === selectedCategory)
          .map(p => ({ email: p.email, name: `${p.first_name} ${p.last_name}` }));
      case 'selected':
        return players
          .filter(p => selectedPlayerIds.has(p.id))
          .map(p => ({ email: p.email, name: `${p.first_name} ${p.last_name}` }));
      case 'custom':
        return customEmails
          .split(/[,;\n]+/)
          .map(e => e.trim())
          .filter(e => e.includes('@'))
          .map(e => ({ email: e, name: '' }));
      default:
        return [];
    }
  };

  const recipientCount = getRecipients().length;

  const sendEmail = async (to: string, recipientName = ''): Promise<SendResult> => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token ?? import.meta.env.VITE_SUPABASE_ANON_KEY;

      const nameParts = recipientName.trim().split(/\s+/);
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      const recipientPlaceholders = { ...placeholders, firstName, lastName };

      const payload: Record<string, unknown> = {
        to,
        placeholders: recipientPlaceholders,
      };

      if (selectedTemplate) {
        payload.emailType = selectedTemplate.id;
        payload.subjectOverride = subject;
      } else {
        payload.emailType = 'announcement';
        payload.placeholders = { ...recipientPlaceholders, headline: subject, message: body };
        payload.subjectOverride = subject;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-email`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        return { email: to, success: false, message: text.slice(0, 200) };
      }

      const result = await response.json();
      return { email: to, success: result.success, message: result.message || 'Sent' };
    } catch (err: any) {
      return { email: to, success: false, message: err.message || 'Unknown error' };
    }
  };

  const handleSendTest = async () => {
    if (!testEmail.trim()) {
      toast.error('Enter a test email address');
      return;
    }
    setSending(true);
    const result = await sendEmail(testEmail.trim(), '');
    if (result.success) {
      toast.success(`Test email sent to ${testEmail}`);
    } else {
      toast.error(`Failed: ${result.message}`);
    }
    setSending(false);
  };

  const handleSendAll = async () => {
    const recipients = getRecipients();
    if (recipients.length === 0) {
      toast.error('No recipients selected');
      return;
    }

    if (!subject.trim() && !selectedTemplate) {
      toast.error('Please enter a subject');
      return;
    }

    if (scheduleEnabled) {
      if (!scheduleDate || !scheduleTime) {
        toast.error('Please set a schedule date and time');
        return;
      }
      const scheduledAt = new Date(`${scheduleDate}T${scheduleTime}`);
      if (scheduledAt <= new Date()) {
        toast.error('Scheduled time must be in the future');
        return;
      }

      setSending(true);
      const { error } = await supabase.from('sent_messages').insert({
        type: 'email',
        subject: subject || selectedTemplate?.defaultSubject || '',
        body: body || '',
        template_id: selectedTemplate?.id || null,
        recipients: recipients,
        recipient_count: recipients.length,
        status: 'scheduled',
        sent_at: scheduledAt.toISOString(),
        is_test: false,
      });

      if (error) {
        toast.error('Failed to schedule message');
      } else {
        toast.success(`Message scheduled for ${scheduledAt.toLocaleString()}`);
      }
      setSending(false);
      return;
    }

    setSending(true);
    setSendResults([]);
    setShowResults(true);

    const results: SendResult[] = [];
    for (const recipient of recipients) {
      const result = await sendEmail(recipient.email, recipient.name);
      results.push(result);
      setSendResults([...results]);
    }

    const successCount = results.filter(r => r.success).length;
    const failCount = results.filter(r => !r.success).length;

    if (failCount === 0) {
      toast.success(`All ${successCount} emails sent successfully`);
    } else {
      toast(`${successCount} sent, ${failCount} failed`, { icon: '!' });
    }

    setSending(false);
  };

  return (
    <div className="px-4 pt-4 space-y-4">
      {/* Template selector */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
        <label className="block text-xs font-medium text-gray-500 mb-2">Template</label>
        <div className="relative">
          <button
            onClick={() => setShowTemplateDropdown(!showTemplateDropdown)}
            className="w-full flex items-center justify-between px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-left"
          >
            <div className="flex items-center gap-2 min-w-0">
              <DocumentTextIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <span className={`truncate ${selectedTemplate ? 'text-gray-800 font-medium' : 'text-gray-400'}`}>
                {selectedTemplate ? selectedTemplate.name : 'Select template (optional)'}
              </span>
            </div>
            <ChevronDownIcon className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${showTemplateDropdown ? 'rotate-180' : ''}`} />
          </button>

          {showTemplateDropdown && (
            <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-56 overflow-y-auto">
              <button
                onClick={() => { setSelectedTemplate(null); setShowTemplateDropdown(false); }}
                className="w-full px-3 py-2.5 text-left text-sm text-gray-500 border-b border-gray-50"
              >
                No template
              </button>
              {TEMPLATE_TYPES.map(template => (
                <button
                  key={template.id}
                  onClick={() => { setSelectedTemplate(template); setShowTemplateDropdown(false); }}
                  className={`w-full px-3 py-2.5 text-left ${selectedTemplate?.id === template.id ? 'bg-golf-orange/5' : ''}`}
                >
                  <p className="text-sm font-medium text-gray-800">{template.name}</p>
                  <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{template.description}</p>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Subject & Body */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 space-y-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1.5">Subject</label>
          <input
            type="text"
            value={subject}
            onChange={e => setSubject(e.target.value)}
            placeholder="Email subject..."
            className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-golf-orange"
          />
        </div>

        {selectedTemplate ? (
          <div className="space-y-3">
            <label className="block text-xs font-medium text-gray-500">Placeholders</label>
            {selectedTemplate.placeholders.map(ph => (
              <div key={ph.key}>
                <label className="block text-[11px] text-gray-400 mb-1">{ph.label}</label>
                <textarea
                  value={placeholders[ph.key] || ''}
                  onChange={e => setPlaceholders({ ...placeholders, [ph.key]: e.target.value })}
                  placeholder={ph.example}
                  rows={2}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-golf-orange resize-y"
                />
              </div>
            ))}
          </div>
        ) : (
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">Message Body</label>
            <textarea
              value={body}
              onChange={e => setBody(e.target.value)}
              placeholder="Compose your message (HTML supported)..."
              rows={8}
              className="w-full px-3 py-3 border border-gray-200 rounded-xl text-sm font-mono focus:outline-none focus:border-golf-orange resize-y"
            />
          </div>
        )}
      </div>

      {/* Recipients */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
        <div className="flex items-center gap-2 mb-3">
          <UserGroupIcon className="w-4 h-4 text-golf-green" />
          <h3 className="text-sm font-semibold text-gray-700">Recipients</h3>
          <span className="ml-auto text-xs font-bold text-golf-green bg-green-50 px-2 py-0.5 rounded-full">{recipientCount}</span>
        </div>

        <div className="space-y-1.5">
          {[
            { value: 'all' as const, label: 'All players', count: players.length },
            { value: 'active' as const, label: 'Active only', count: players.filter(p => p.status?.toLowerCase() === 'active').length },
            { value: 'category' as const, label: 'By category', count: null },
            { value: 'selected' as const, label: 'Select players', count: selectedPlayerIds.size || null },
            { value: 'custom' as const, label: 'Custom list', count: null },
          ].map(option => (
            <label
              key={option.value}
              className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl border cursor-pointer transition-colors ${
                recipientFilter === option.value
                  ? 'border-golf-orange bg-golf-orange/5'
                  : 'border-gray-100'
              }`}
            >
              <input
                type="radio"
                name="recipientFilter"
                value={option.value}
                checked={recipientFilter === option.value}
                onChange={() => setRecipientFilter(option.value)}
                className="text-golf-orange focus:ring-golf-orange"
              />
              <span className="text-sm text-gray-700 flex-1">{option.label}</span>
              {option.count !== null && (
                <span className="text-[11px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">{option.count}</span>
              )}
            </label>
          ))}
        </div>

        {recipientFilter === 'category' && (
          <select
            value={selectedCategory}
            onChange={e => setSelectedCategory(e.target.value)}
            className="mt-3 w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-golf-orange bg-white"
          >
            <option value="">Select category...</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        )}

        {recipientFilter === 'selected' && (
          <div className="mt-3 space-y-2">
            <div className="relative">
              <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={playerSearch}
                onChange={e => setPlayerSearch(e.target.value)}
                placeholder="Search players..."
                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-golf-orange"
              />
            </div>
            <div className="flex items-center justify-between px-1">
              <button
                onClick={() => {
                  const filtered = players.filter(p => {
                    if (!playerSearch) return true;
                    const q = playerSearch.toLowerCase();
                    return `${p.first_name} ${p.last_name}`.toLowerCase().includes(q) || p.email.toLowerCase().includes(q);
                  });
                  setSelectedPlayerIds(new Set(filtered.map(p => p.id)));
                }}
                className="text-xs text-golf-orange"
              >
                Select all
              </button>
              <button
                onClick={() => setSelectedPlayerIds(new Set())}
                className="text-xs text-gray-400"
              >
                Clear
              </button>
            </div>
            <div className="max-h-40 overflow-y-auto border border-gray-100 rounded-xl divide-y divide-gray-50">
              {players
                .filter(p => {
                  if (!playerSearch) return true;
                  const q = playerSearch.toLowerCase();
                  return `${p.first_name} ${p.last_name}`.toLowerCase().includes(q) || p.email.toLowerCase().includes(q);
                })
                .map(player => (
                  <label
                    key={player.id}
                    className="flex items-center gap-2 px-3 py-2 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedPlayerIds.has(player.id)}
                      onChange={() => {
                        const next = new Set(selectedPlayerIds);
                        if (next.has(player.id)) next.delete(player.id);
                        else next.add(player.id);
                        setSelectedPlayerIds(next);
                      }}
                      className="rounded text-golf-orange focus:ring-golf-orange"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-700 truncate">{player.first_name} {player.last_name}</p>
                      <p className="text-[10px] text-gray-400 truncate">{player.email}</p>
                    </div>
                  </label>
                ))}
            </div>
          </div>
        )}

        {recipientFilter === 'custom' && (
          <textarea
            value={customEmails}
            onChange={e => setCustomEmails(e.target.value)}
            placeholder="Enter emails (comma, semicolon, or newline separated)"
            rows={3}
            className="mt-3 w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-golf-orange resize-y"
          />
        )}
      </div>

      {/* Schedule */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
        <label className="flex items-center gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            checked={scheduleEnabled}
            onChange={e => setScheduleEnabled(e.target.checked)}
            className="rounded text-golf-orange focus:ring-golf-orange"
          />
          <ClockIcon className="w-4 h-4 text-gray-400" />
          <span className="text-sm font-medium text-gray-700">Schedule for later</span>
        </label>

        {scheduleEnabled && (
          <div className="mt-3 grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[11px] text-gray-400 mb-1">Date</label>
              <input
                type="date"
                value={scheduleDate}
                onChange={e => setScheduleDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-golf-orange"
              />
            </div>
            <div>
              <label className="block text-[11px] text-gray-400 mb-1">Time</label>
              <input
                type="time"
                value={scheduleTime}
                onChange={e => setScheduleTime(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-golf-orange"
              />
            </div>
          </div>
        )}
      </div>

      {/* Test email */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
        <div className="flex items-center gap-2 mb-2">
          <BeakerIcon className="w-4 h-4 text-golf-green" />
          <h3 className="text-xs font-semibold text-gray-600">Test Email</h3>
        </div>
        <div className="flex gap-2">
          <input
            type="email"
            value={testEmail}
            onChange={e => setTestEmail(e.target.value)}
            placeholder="test@example.com"
            className="flex-1 px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-golf-orange"
          />
          <button
            onClick={handleSendTest}
            disabled={sending}
            className="px-3 py-2.5 bg-gray-700 text-white rounded-xl text-xs font-medium disabled:opacity-50"
          >
            {sending ? '...' : 'Test'}
          </button>
        </div>
      </div>

      {/* Send button */}
      <button
        onClick={handleSendAll}
        disabled={sending || recipientCount === 0}
        className="w-full py-3.5 bg-golf-orange text-white rounded-2xl font-semibold text-sm disabled:opacity-50 transition-colors flex items-center justify-center gap-2 shadow-sm"
      >
        {scheduleEnabled ? <ClockIcon className="w-4 h-4" /> : <PaperAirplaneIcon className="w-4 h-4" />}
        {sending
          ? 'Processing...'
          : scheduleEnabled
            ? `Schedule (${recipientCount})`
            : `Send to ${recipientCount} recipient${recipientCount !== 1 ? 's' : ''}`
        }
      </button>

      {/* Results */}
      {showResults && sendResults.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-xs font-semibold text-gray-600 uppercase">Results</h4>
            <button onClick={() => setShowResults(false)}>
              <XMarkIcon className="w-4 h-4 text-gray-400" />
            </button>
          </div>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {sendResults.map((result, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                {result.success ? (
                  <CheckCircleIcon className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                ) : (
                  <ExclamationCircleIcon className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                )}
                <span className="text-gray-600 truncate flex-1">{result.email}</span>
              </div>
            ))}
          </div>
          <div className="mt-2 pt-2 border-t border-gray-50 flex gap-3 text-xs">
            <span className="text-green-600">{sendResults.filter(r => r.success).length} sent</span>
            <span className="text-red-500">{sendResults.filter(r => !r.success).length} failed</span>
          </div>
        </div>
      )}
    </div>
  );
}

function MobilePushComposer() {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [action, setAction] = useState('');
  const [customUrl, setCustomUrl] = useState('');
  const [sending, setSending] = useState(false);

  const actionUrl = action === 'custom' ? customUrl : action;

  const handleSendAll = async () => {
    if (!title.trim() || !message.trim()) {
      toast.error('Please enter a title and message');
      return;
    }
    setSending(true);
    await new Promise(resolve => setTimeout(resolve, 1500));
    toast.success('Push notification sent to all devices');
    setSending(false);
  };

  return (
    <div className="px-4 pt-4 space-y-4">
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 space-y-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1.5">Title</label>
          <input
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="e.g. Round 2 Starting Soon!"
            maxLength={65}
            className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-golf-orange"
          />
          <p className="text-[11px] text-gray-400 mt-1">{title.length}/65</p>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1.5">Message</label>
          <textarea
            value={message}
            onChange={e => setMessage(e.target.value)}
            placeholder="Notification message..."
            rows={3}
            maxLength={240}
            className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-golf-orange resize-y"
          />
          <p className="text-[11px] text-gray-400 mt-1">{message.length}/240</p>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1.5">Tap Action</label>
          <select
            value={action}
            onChange={e => setAction(e.target.value)}
            className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-golf-orange bg-white"
          >
            {PUSH_ACTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {action === 'custom' && (
          <input
            type="text"
            value={customUrl}
            onChange={e => setCustomUrl(e.target.value)}
            placeholder="/m/custom-page or https://..."
            className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-golf-orange"
          />
        )}
      </div>

      {/* Preview */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
        <h3 className="text-xs font-semibold text-gray-500 uppercase mb-3">Preview</h3>
        <div className="bg-gray-900 rounded-2xl p-3">
          <div className="bg-white rounded-xl p-3 shadow-sm">
            <div className="flex items-start gap-2">
              <div className="w-7 h-7 rounded-lg bg-golf-green flex items-center justify-center flex-shrink-0">
                <span className="text-white text-[10px] font-bold">IO</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <p className="text-[11px] font-semibold text-gray-900 truncate">Ibom Open</p>
                  <span className="text-[9px] text-gray-400">now</span>
                </div>
                <p className="text-[11px] font-medium text-gray-800 mt-0.5 truncate">
                  {title || 'Notification title'}
                </p>
                <p className="text-[10px] text-gray-500 mt-0.5 line-clamp-2">
                  {message || 'Message preview...'}
                </p>
              </div>
            </div>
          </div>
          {actionUrl && (
            <p className="text-[9px] text-gray-400 mt-1.5 px-1">
              Opens: <span className="text-golf-orange">{actionUrl}</span>
            </p>
          )}
        </div>
      </div>

      {/* Send */}
      <button
        onClick={handleSendAll}
        disabled={sending || !title.trim() || !message.trim()}
        className="w-full py-3.5 bg-golf-orange text-white rounded-2xl font-semibold text-sm disabled:opacity-50 transition-colors flex items-center justify-center gap-2 shadow-sm"
      >
        <PaperAirplaneIcon className="w-4 h-4" />
        {sending ? 'Sending...' : 'Send to All Devices'}
      </button>

      <div className="bg-blue-50 rounded-2xl p-3 border border-blue-100">
        <p className="text-[11px] text-blue-700 leading-relaxed">
          Push notifications are delivered to all users with the app installed and notifications enabled.
        </p>
      </div>
    </div>
  );
}
